/**
 * Detalhe/edição de evento do catálogo (U4) — modal de edição com flags como
 * Switch de rótulo claro, desativar com confirmação e criação guiada. Regras de
 * proteção vêm da API (409 legível exibido no modal): em uso não exclui;
 * tipo/unidade/código não mudam se usado em competência fechada; a edição vale
 * "daqui pra frente" (sem recálculo do passado).
 */
import { useEffect, useState } from "react";
import { Button, Group, Modal, NumberInput, Select, Switch, Text, TextInput } from "@mantine/core";
import { api, ErroApi } from "../../api";
import { Confirmacao, notificarOk, notificarErroApi } from "../../componentes/notificacoes";

export interface Evento {
  id: string;
  codigoDominio: number | null;
  idConvenia: number | null;
  descricao: string;
  tipo: string | null;
  unidade: string | null;
  incideInss: boolean | null;
  incideIrrf: boolean | null;
  incideFgts: boolean | null;
  exportaDominio: boolean;
  exportaConvenia: boolean;
  ativo: boolean;
  observacao: string | null;
  formula: { id: string; versao: string } | null;
  /** 27/08/2026 — config de média (engine Domínio): "horas" (× valor-hora atual
   *  × taxa) | "valor" (média dos pagos) | null (não integra médias). */
  mediaUnidade?: string | null;
  mediaTaxaPpm?: number | null;
}
interface FormulaOpcao {
  id: string;
  chave: string;
  versao: string;
}

const TRI = [
  { value: "null", label: "— (não mapeado)" },
  { value: "true", label: "sim" },
  { value: "false", label: "não" },
];

function TriSelect({ label, description, valor, onChange }: { label: string; description?: string; valor: boolean | null; onChange: (v: boolean | null) => void }) {
  return (
    <Select
      label={label}
      description={description}
      data={TRI}
      value={valor === null ? "null" : String(valor)}
      onChange={(v) => onChange(v === "null" ? null : v === "true")}
    />
  );
}

export function EventoDetalhe({
  evento,
  formulas,
  onFechar,
  onSalvo,
}: {
  /** null = modal de CRIAÇÃO de evento novo. */
  evento: Evento | null;
  formulas: FormulaOpcao[];
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const criacao = evento === null;
  const [codigo, setCodigo] = useState<number | null>(null);
  const [convenia, setConvenia] = useState<number | null>(null);
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState<string | null>(null);
  const [unidade, setUnidade] = useState<string | null>(null);
  const [inss, setInss] = useState<boolean | null>(null);
  const [irrf, setIrrf] = useState<boolean | null>(null);
  const [fgts, setFgts] = useState<boolean | null>(null);
  const [expDom, setExpDom] = useState(false);
  const [expConv, setExpConv] = useState(false);
  const [formulaId, setFormulaId] = useState<string | null>(null);
  const [observacao, setObservacao] = useState("");
  const [mediaUnidade, setMediaUnidade] = useState<string | null>(null);
  const [mediaTaxa, setMediaTaxa] = useState<number | null>(null); // % inteiro (150 = 150%)
  const [erro, setErro] = useState<string | null>(null);
  const [confirmarDesativar, setConfirmarDesativar] = useState(false);
  const [confirmarExcluir, setConfirmarExcluir] = useState(false);

  useEffect(() => {
    setCodigo(evento?.codigoDominio ?? null);
    setConvenia(evento?.idConvenia ?? null);
    setDescricao(evento?.descricao ?? "");
    setTipo(evento?.tipo ?? null);
    setUnidade(evento?.unidade ?? null);
    setInss(evento?.incideInss ?? null);
    setIrrf(evento?.incideIrrf ?? null);
    setFgts(evento?.incideFgts ?? null);
    setExpDom(evento?.exportaDominio ?? false);
    setExpConv(evento?.exportaConvenia ?? false);
    setFormulaId(evento?.formula?.id ?? null);
    setObservacao(evento?.observacao ?? "");
    setMediaUnidade(evento?.mediaUnidade ?? null);
    setMediaTaxa(evento?.mediaTaxaPpm != null ? evento.mediaTaxaPpm / 10000 : null);
    setErro(null);
  }, [evento]);

  async function salvar() {
    setErro(null);
    try {
      const corpo = {
        codigoDominio: codigo,
        idConvenia: convenia,
        descricao,
        tipo,
        unidade,
        incideInss: inss,
        incideIrrf: irrf,
        incideFgts: fgts,
        exportaDominio: expDom,
        exportaConvenia: expConv,
        formulaId,
        observacao: observacao || null,
        // config de média: só na EDIÇÃO (criação segue sem flags — ajuste depois)
        ...(!criacao ? { mediaUnidade, mediaTaxaPpm: mediaUnidade === "horas" && mediaTaxa !== null ? Math.round(mediaTaxa * 10000) : null } : {}),
      };
      if (criacao) {
        await api.post("/eventos", corpo);
        notificarOk("Evento criado.");
      } else {
        await api.patch(`/eventos/${evento!.id}`, corpo);
        notificarOk("Evento atualizado (vale daqui pra frente).");
      }
      onSalvo();
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  async function desativar() {
    try {
      await api.post(`/eventos/${evento!.id}/desativar`);
      notificarOk("Evento desativado (reversível).");
      onSalvo();
      onFechar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  async function excluir() {
    try {
      await api.del(`/eventos/${evento!.id}`);
      notificarOk("Evento excluído (nunca tinha sido usado).");
      onSalvo();
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  return (
    <Modal
      opened
      onClose={onFechar}
      title={criacao ? "Novo evento" : `Evento ${evento!.codigoDominio ?? "s/código"} — ${evento!.descricao}`}
      size="lg"
      centered
    >
      <Text size="xs" c="dimmed" mb="sm">
        Edição vale daqui pra frente: nada é recalculado no passado. Tipo/unidade/código ficam travados quando o evento já
        foi usado em competência fechada (a API explica com 409).
      </Text>
      <Group grow mb="sm">
        <NumberInput label="Código Domínio" description="Chave de matching com o retorno da contabilidade." value={codigo ?? ""} onChange={(v) => setCodigo(typeof v === "number" ? v : null)} hideControls min={0} />
        <NumberInput label="ID Convenia" description="Identificador no Convenia (importação de variáveis)." value={convenia ?? ""} onChange={(v) => setConvenia(typeof v === "number" ? v : null)} hideControls min={0} />
      </Group>
      <TextInput label="Descrição" value={descricao} onChange={(e) => setDescricao(e.currentTarget.value.toUpperCase())} mb="sm" required />
      <Group grow mb="sm">
        <Select label="Tipo" data={[{ value: "P", label: "P — provento" }, { value: "D", label: "D — desconto" }]} value={tipo} onChange={setTipo} clearable placeholder="—" />
        <Select
          label="Unidade da referência"
          data={[
            { value: "horas", label: "horas" },
            { value: "percentual", label: "percentual" },
            { value: "valor", label: "valor (R$)" },
          ]}
          value={unidade}
          onChange={setUnidade}
          clearable
          placeholder="—"
        />
      </Group>
      <Group grow mb="sm">
        <TriSelect label="Incide INSS" valor={inss} onChange={setInss} />
        <TriSelect label="Incide IRRF" valor={irrf} onChange={setIrrf} />
        <TriSelect label="Incide FGTS" valor={fgts} onChange={setFgts} />
      </Group>
      <Group mb="sm">
        <Switch label="Exporta p/ Domínio (TXT)" checked={expDom} onChange={(e) => setExpDom(e.currentTarget.checked)} />
        <Switch label="Exporta p/ Convenia (xlsx)" checked={expConv} onChange={(e) => setExpConv(e.currentTarget.checked)} />
      </Group>
      <Select
        label="Fórmula associada"
        description="Qual fórmula do motor produz este evento (rastreabilidade)."
        data={formulas.map((f) => ({ value: f.id, label: `${f.chave} (${f.versao})` }))}
        value={formulaId}
        onChange={setFormulaId}
        clearable
        searchable
        mb="sm"
      />
      <TextInput label="Observação" value={observacao} onChange={(e) => setObservacao(e.currentTarget.value)} mb="sm" />
      {!criacao && (
        <Group grow mb="sm">
          <Select
            label="Integra médias (férias, 13º, aviso)"
            description="Modelo Domínio: em horas = média de horas × valor-hora atual × taxa; em valor = média dos valores pagos. Vazio = não entra na média."
            data={[
              { value: "horas", label: "sim — em horas" },
              { value: "valor", label: "sim — em valor (R$)" },
            ]}
            value={mediaUnidade}
            onChange={setMediaUnidade}
            clearable
            placeholder="não integra"
          />
          {mediaUnidade === "horas" && (
            <NumberInput
              label="Taxa da rubrica (%)"
              description="Ex.: 150 para HE 50%; 200 para HE 100%; 20 para adicional noturno."
              value={mediaTaxa ?? ""}
              onChange={(v) => setMediaTaxa(typeof v === "number" ? v : null)}
              hideControls
              min={1}
            />
          )}
        </Group>
      )}
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      <Group justify="space-between">
        <Group>
          {!criacao && evento!.ativo && (
            <Button variant="subtle" color="yellow.8" onClick={() => setConfirmarDesativar(true)}>
              Desativar
            </Button>
          )}
          {!criacao && (
            <Button variant="subtle" color="red.7" onClick={() => setConfirmarExcluir(true)}>
              Excluir
            </Button>
          )}
        </Group>
        <Group>
          <Button variant="default" onClick={onFechar}>
            Voltar
          </Button>
          <Button onClick={() => void salvar()} disabled={!descricao.trim()}>
            {criacao ? "Criar evento" : "Salvar alterações"}
          </Button>
        </Group>
      </Group>

      <Confirmacao
        aberta={confirmarDesativar}
        titulo={'Desativar "' + (evento?.descricao ?? "") + '"?'}
        consequencia="O evento some das telas de seleção, mas continua no histórico (runs e exports antigos não mudam). Reversível: dá para reativar editando depois."
        rotuloConfirmar="Desativar"
        onConfirmar={() => {
          setConfirmarDesativar(false);
          void desativar();
        }}
        onCancelar={() => setConfirmarDesativar(false)}
      />
      <Confirmacao
        aberta={confirmarExcluir}
        titulo={'Excluir "' + (evento?.descricao ?? "") + '"?'}
        consequencia="Só funciona se o evento NUNCA foi usado em nenhuma execução — a API confere e bloqueia com explicação se já tiver uso (nesse caso o caminho é desativar)."
        rotuloConfirmar="Excluir"
        onConfirmar={() => {
          setConfirmarExcluir(false);
          void excluir();
        }}
        onCancelar={() => setConfirmarExcluir(false)}
      />
    </Modal>
  );
}
